import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const SunburstGraph = ({ data }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [currentRoot, setCurrentRoot] = useState(null);

  // Retrieve the project name from session storage
  const storedData = JSON.parse(sessionStorage.getItem('shipmentData'));
  const projectName = storedData?.project_name || 'Project Name';

  // Transform flat data into a hierarchical structure
  const hierarchicalData = {
    name: projectName, // Fixed: Changed project_name to projectName
    children: data.map((item) => ({
      name: `${item.supplier_name} - ${item.module_wattage}W`,
      children: [
        { name: 'Module Count PDI', value: item.module_count_pdi },
        { name: 'Total Planned Modules', value: item.total_planned_modules },
        { name: 'No. of Modules Shipped', value: item.no_of_modules_shipped },
      ],
    })),
  };

  // Function to truncate long text
  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  };

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    // Get container dimensions for responsiveness
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    const radius = Math.min(width, height) / 2;

    // Clear previous SVG content
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height);

    // Initialize currentRoot on first render
    if (!currentRoot) {
      setCurrentRoot(hierarchicalData);
      return;
    }

    // Create hierarchy
    const hierarchy = d3
      .hierarchy(currentRoot)
      .sum((d) => d.value || 0)
      .sort((a, b) => b.value - a.value);

    // Create partition layout
    const partition = d3.partition().size([2 * Math.PI, radius]);
    const root = partition(hierarchy);

    // Create arc generator
    const arc = d3
      .arc()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .padAngle((d) => Math.min((d.x1 - d.x0) / 2, 0.005))
      .padRadius(radius / 2)
      .innerRadius((d) => d.y0)
      .outerRadius((d) => d.y1 - 1);

    // Create color scale
    const color = d3.scaleOrdinal(d3.schemePaired);

    // Create SVG group
    const g = svg
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    // Draw arcs with animation
    const path = g
      .selectAll('path')
      .data(root.descendants().filter((d) => d.depth))
      .enter()
      .append('path')
      .attr('fill', (d) => {
        const baseColor = color(d.data.name);
        return d3.color(baseColor).darker(0.3 * d.depth);
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        if (d.children) {
          setCurrentRoot(d.data);
        }
      })
      .on('mouseover', function (event, d) {
        d3.select(this).attr('fill-opacity', 0.8);
        const tooltip = d3
          .select('body')
          .append('div')
          .attr('class', 'tooltip')
          .style('position', 'absolute')
          .style('background', '#333')
          .style('color', '#fff')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('pointer-events', 'none')
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY + 10}px`)
          .html(`<strong>${d.data.name}</strong><br/>Value: ${d.value || 0}`);
      })
      .on('mousemove', (event) => {
        d3.select('.tooltip')
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY + 10}px`);
      })
      .on('mouseout', function () {
        d3.select(this).attr('fill-opacity', 1);
        d3.selectAll('.tooltip').remove();
      });

    // Animate arcs
    path
      .transition()
      .duration(750)
      .ease(d3.easeCubicInOut)
      .attrTween('d', (d) => {
        const i = d3.interpolate({ x0: d.x0, x1: d.x0, y0: d.y0, y1: d.y0 }, d);
        return (t) => arc(i(t));
      });

    // Add responsive labels
    g.selectAll('text.arc-label')
      .data(root.descendants().filter((d) => d.depth && (d.x1 - d.x0) > 0.05))
      .enter()
      .append('text')
      .attr('class', 'arc-label')
      .attr('transform', (d) => {
        const x = ((d.x0 + d.x1) / 2) * 180 / Math.PI;
        const y = (d.y0 + d.y1) / 2;
        return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
      })
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .attr('fill', '#fff')
      .attr('font-size', (d) => {
        const angle = d.x1 - d.x0;
        const arcWidth = (d.y1 - d.y0) * angle; // Approximate segment width
        const baseSize = Math.min(width / 60, 10); // Smaller base size
        const fontSize = Math.max(6, Math.min(baseSize * Math.sqrt(angle), arcWidth / (d.data.name.length * 0.5)));
        return `${fontSize}px`;
      })
      .text((d) => truncateText(d.data.name, 15)) // Truncate to 15 chars
      .transition()
      .duration(750)
      .attr('opacity', 1);

    // Add center project name label
    g.append('text')
      .attr('class', 'project-label')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('font-size', `${Math.min(width / 30, 14)}px`)
      .attr('fill', '#1f2937')
      .style('pointer-events', 'none')
      .text(truncateText(projectName, 20)) // Truncate project name if too long
      .transition()
      .duration(750)
      .attr('opacity', 1);

    // Reset button
    g.append('text')
      .attr('class', 'reset-button')
      .attr('text-anchor', 'middle')
      .attr('dy', '1.5em')
      .attr('font-size', `${Math.min(width / 35, 12)}px`)
      .attr('fill', '#1f2937')
      .style('cursor', 'pointer')
      .text('Reset')
      .on('click', () => setCurrentRoot(hierarchicalData))
      .transition()
      .duration(750)
      .attr('opacity', 1);

    // Handle window resize
    const handleResize = () => {
      setCurrentRoot({ ...currentRoot }); // Trigger re-render
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentRoot, data]);

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-[500px] h-[400px] sm:h-[600px] bg-white p-4 sm:p-6 rounded-lg mx-auto"
    >
      {/* <h1 className="text-xl sm:text-2xl font-bold mb-4 text-center text-gray-800">
        Sunburst Graph
      </h1> */}
      <svg
        ref={svgRef}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      ></svg>
    </div>
  );
};

export default SunburstGraph;


// import React from "react";
// import { ResponsiveSunburst } from "@nivo/sunburst";

// const SunburstGraph = ({ data }) => {
//   // Retrieve the project name from session storage
//   const storedData = JSON.parse(sessionStorage.getItem("shipmentData"));
//   const projectName = storedData?.project_name || "Project Name"; // Default to "Project Name" if not found

//   // Transform flat data into a hierarchical structure
//   const hierarchicalData = {
//     name: projectName, // Project name as the root node
//     children: data.map((item) => ({
//       name: `${item.supplier_name} - ${item.module_wattage}W`,
//       children: [
//         { name: "Module Count PDI", value: item.module_count_pdi },
//         { name: "Total Planned Modules", value: item.total_planned_modules },
//         { name: "No. of Modules Shipped", value: item.no_of_modules_shipped },
//       ],
//     })),
//   };

//   return (
//     <div style={{ height: 500, position: "relative" }}>
//       <ResponsiveSunburst
//         data={hierarchicalData}
//         margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
//         id="name"
//         value="value"
//         cornerRadius={3}
//         borderWidth={1}
//         borderColor="#fff"
//         colors={{ scheme: "accent" }}
//         childColor={{ from: "color", modifiers: [["darker", 0.5]] }}
//         enableArcLabels={true}
//         arcLabel={(d) => `${d.id}\n${d.value}`}

//         arcLabelsSkipAngle={10}
//         arcLabelsTextColor={{
//           from: "color",
//           modifiers: [["darker", 2]],
//         }}
//         tooltip={({ id, value }) => (
//           <div
//             style={{
//               padding: "8px",
//               backgroundColor: "#333",
//               color: "#fff",
//               borderRadius: "4px",
//             }}
//           >
//             <strong>{id}</strong> 
//             <br />
//             Value: {value}
//           </div>
//         )}
//       />
//       <div
//         style={{
//           position: "absolute",
//           top: "50%",
//           left: "50%",
//           transform: "translate(-50%, -50%)",
//           textAlign: "center",
//           pointerEvents: "none", // Ensure it doesn't block interactions
//           backgroundColor: "rgba(255, 255, 255, 0.8)", // Semi-transparent background
//           padding: "8px",
//           borderRadius: "4px",
//         }}
//       >
//         <div
//           style={{
//             fontSize: "16px",
//             fontWeight: "bold",
//             color: "#424752", 
//           }}
//         >
//           {projectName}
//         </div>
//       </div>
//     </div>
      
//   );
// };

// export default SunburstGraph;


